import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar, Users, ArrowRight, Sparkles, Heart, Play, HelpCircle, Timer, CheckSquare } from "lucide-react";

const faqData = [
  {
    question: "What happened to Sunrise Calendar?",
    answer: "Sunrise Calendar was discontinued on August 31, 2016 after Microsoft acquired it for ~$100 million. CSW offers free productivity tools as an alternative."
  },
  {
    question: "Is CSW a free Sunrise Calendar alternative?",
    answer: "While CSW isn't a calendar app, it offers complementary productivity tools including task management, timers, and reminders to help you execute on your schedule."
  },
  {
    question: "What features does CSW offer?",
    answer: "CSW offers 24/7 virtual study rooms, customizable Pomodoro timers, Task Management with Kanban boards, and reminders—all free."
  },
  {
    question: "Can I schedule study sessions on CSW?",
    answer: "Yes! CSW lets you join or host live productivity sessions, helping you follow through on your calendar plans."
  },
  {
    question: "Does CSW have task management like Sunrise integrated?",
    answer: "Yes, CSW has robust task management with to-do lists, Kanban boards, and reminders to keep you organized."
  },
  {
    question: "Is CSW free?",
    answer: "Yes, CSW is completely free with unlimited access to all features—no subscription required."
  }
];

const SunriseCalendar = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Sunrise Calendar Alternative - Free Productivity Tools"
        description="Looking for a Sunrise Calendar alternative? Sunrise shut down in 2016 but CSW offers free productivity tools with Task Management, Study Timer, and Reminders."
        keywords="sunrise calendar alternative, sunrise app, calendar app, productivity tools, task management, microsoft outlook"
        canonicalUrl="/sunrise-calendar/"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Sunrise Calendar Alternative - Free Productivity Tools | CSW",
        "description": "Looking for a Sunrise Calendar alternative? CSW offers free productivity tools.",
        "url": "https://csw.live/sunrise-calendar/",
        "inLanguage": "en",
        "isPartOf": { "@type": "WebSite", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
        "about": { "@type": "Thing", "name": "Sunrise Calendar Alternative" },
        "keywords": "sunrise calendar alternative, sunrise app, calendar app, productivity tools"
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - Sunrise Calendar Alternative",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "1000" },
        "featureList": ["Task Management", "Pomodoro Timer", "Reminders", "Study Rooms", "Kanban Boards"],
        "publisher": { "@type": "Organization", "name": "CSW", "url": "https://csw.live" }
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } }))
      })}} />
      
      <Header />

      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        
        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-gradient">Sunrise Calendar</span>
            </h1>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Sunrise Calendar Alternative</h1>
            <h2 className="font-display text-2xl sm:text-3xl text-muted-foreground">Free Productivity Tools <span className="text-gradient">24/7</span></h2>
            
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              When Microsoft shut down Sunrise Calendar in 2016, millions lost "the best calendar app ever." CSW helps you execute on your schedule with free productivity tools. <Link to="/study-with-me/" className="text-primary hover:underline">Join our community</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 px-8 py-6 text-lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span>Free Forever</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-8 bg-card/50 border-primary/20">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-primary" />
              </div>
              <div className="space-y-4">
                <h2 className="text-3xl font-bold">Sunrise Calendar Has <span className="text-gradient">Shut Down</span></h2>
                <p className="text-muted-foreground text-lg">
                  Sunrise Calendar—the beautifully designed app that raised the bar for digital calendars—was discontinued on August 31, 2016 after Microsoft acquired it for ~$100 million. <Link to="/sunrise-calendar/history/" className="text-primary hover:underline font-medium">Read the full Sunrise story →</Link>
                </p>
                <p className="text-muted-foreground text-lg">
                  While CSW isn't a calendar, it helps you <span className="font-semibold text-foreground">execute on your schedule</span> with <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link>, <Link to="/study-timer/" className="text-primary hover:underline">Study Timer</Link>, and <Link to="/reminders/" className="text-primary hover:underline">Reminders</Link>.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">Task <span className="text-gradient">Management</span></h2>
              <p className="text-muted-foreground text-lg">
                Sunrise integrated with task apps like Wunderlist. CSW has <Link to="/task-management/" className="text-primary hover:underline">built-in task management</Link> with Kanban boards and to-do lists.
              </p>
              <p className="text-muted-foreground text-lg">
                Learn about <Link to="/blog/apps-boost-organization-college/" className="text-primary hover:underline">apps to boost organization</Link>.
              </p>
            </div>
            <Card className="p-8 bg-card/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 bg-primary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Timer className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">Study <span className="text-gradient">Timer</span></h2>
              <p className="text-muted-foreground text-lg">
                Use CSW's <Link to="/study-timer/" className="text-primary hover:underline">Pomodoro timer</Link> to structure your work sessions and stay focused on your calendar goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Frequently Asked <span className="text-gradient">Questions</span></h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg border px-6">
                  <AccordionTrigger className="text-left font-medium py-4">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
        <div className="container relative z-10 mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to <span className="text-gradient">Get Started?</span></h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands using CSW to stay productive and organized.
          </p>
          <Button size="lg" asChild>
            <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
              Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SunriseCalendar;
